import React, { Component, Fragment, ReactNode } from 'react';
import FormicaLabel from '../../../custom/FormicaLabel';
import { RootState } from '../../../../redux/store';
import { VelocityVariableState } from '../../../../redux/reducers/VelocityVariableReducer';
import { enterComponent, leaveComponent } from '../../../../redux/actions/ComponentActions';
import { changeVelocityVariable, changeVelocityVariableIsUpdate, clearVelocityVariable } from '../../../../redux/actions/VelocityVariableActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import {
  ArchiosExpressionProfile,
  ExpressionBuilderView,
  ExpressionContextMenuComponent,
  ExpressionTokenComponent,
  StandardExpressionBuilderView,
  Syntax,
  TokenVariable,
  VariableToken,
} from '@trlogic/ui-pack';
import VariableType from '../../../../constant/VariableType';
import CollectionNames from '../../../../constant/CollectionNames';
import { TransactionJsonModel } from '../../../../domain/stream/transaction/TransactionDomains';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../../../../config/binding/InstanceBinding';
import Injectables from '../../../../constant/Injectables';
import _ from 'lodash';
import { EnrichmentBase } from '../../../../domain/enrichment/EnrichmentDomain';
import DataType from '../../../../constant/DataType';
import ExpressionBridge from '../../../../util/ExpressionBridge';
import ServiceContainer from '../../../../config/ServiceContainer';
import { ServiceNames } from '../../../../constant/ServiceNames';
import QueryBuilder = query.QueryBuilder;
import eq = query.eq;
import SubscriptionContext = subscribe.SubscriptionContext;
import Computation = subscribe.Computation;
import LanguageUtil from '../../../../util/LanguageUtil';
import { ExpressionUtil } from '../../../../util/ExpressionUtil';

class VelocityVariableGroupByComponent extends Component<Props, State> {
  public state = new State();

  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  private readonly expressionBridge: ExpressionBridge = ServiceContainer.getInstance().get(ServiceNames.EXPRESSION_BRIDGE);
  private computation: Computation | undefined;
  private subscriptionContext: SubscriptionContext = InstanceContainer.get(Injectables.SUBSCRIPTION_CONTEXT);

  public render(): ReactNode {
    const {
      velocityVariable: { velocityVariable },
    } = this.props;
    const { expression, expressionView } = this.state;
    if (expressionView == undefined) {
      return null;
    }
    return (
      <div className={'flex flex-col gap-y-1.5 w-full shrink-0'}>
        <FormicaLabel label={LanguageUtil.get({ id: 'common.label.groupBy', defaultMessage: 'Group By' })} />
        <div className={'flex flex-col w-full border-[1px] border-[#E0E4E8] h-full overflow-scroll rounded-[4px] px-5 py-5 bg-main-gray'}>
          <div className='' id='expression_builder_group_by'>
            {expression && expressionView && (
              <Fragment>
                <ExpressionTokenComponent syntax={expressionView.getModel()} view={expressionView} />
              </Fragment>
            )}
            <ExpressionContextMenuComponent context={expressionView.getContext()} close={this.onClose} />
          </div>
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    const enrichmentSubscriptions = this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.ENRICHMENTS);
    const transactionSubscriptions = this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.TRANSACTIONS);
    this.computation = subscribe.Tracker.autorun(this.tracker, [...enrichmentSubscriptions, ...transactionSubscriptions]);
    this.build();
  }

  public componentDidUpdate(prevProps: Readonly<Props>): void {
    if (this.props.update != prevProps.update) {
      this.refreshVariables();
    }
    if (this.props.disabled != prevProps.disabled) {
      if (this.props.disabled) {
        this.state.expressionView?.pause();
      } else {
        this.state.expressionView?.resume();
      }
    }
    if (this.props.velocityUpdated != prevProps.velocityUpdated) {
      this.state.expressionView?.terminate();
      this.setState({ expressionView: undefined }, this.build);
    }
  }

  private tracker = async () => {
    const { expressionView } = this.state;
    if (
      this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.ENRICHMENTS).some((e) => e.ready()) ||
      this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.TRANSACTIONS).some((e) => e.ready())
    ) {
      const variables = await this.prepareVariables();
      expressionView?.getContext().setVariables(variables);
    }
  };

  private build = async () => {
    const expression = this.props.velocityVariable.velocityVariable.groupBy;
    const variables: Record<string, TokenVariable[]> = await this.prepareVariables();
    await this.expressionBridge.start(expression, variables);
    const expressionView: ExpressionBuilderView = new StandardExpressionBuilderView({
      el: '#expression_builder_group_by',
      model: expression,
      profile: new ArchiosExpressionProfile(),
      returnType: { dataType: DataType.STRING },
      onCtrlToken: ExpressionUtil.onNavigateToken,
    });
    expressionView.getContext().setVariables(variables);
    expressionView.onChange(this.onChangeSyntax as any);
    this.setState({ expressionView, expression }, () => {
      expressionView.initialize();
      if (this.props.disabled) expressionView.pause();
    });
  };

  private prepareVariables = async (): Promise<Record<string, TokenVariable[]>> => {
    const {
      velocityVariable: { velocityVariable },
    } = this.props;
    const event: TransactionJsonModel | undefined = await this.repository.find(
      QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(eq('name', velocityVariable.transaction)).build(),
    );
    if (event == undefined) return {};
    const eventVariables = event.variables.map((v) => ({
      type: VariableType.EVENT_VARIABLE as any,
      variable: {
        type: 'Common Variable',
        variableType: VariableType.EVENT_VARIABLE,
        properties: v.properties,
        name: v.name,
        description: v.description,
        args: {
          event: event.name,
        },
      },
    }));
    const enrichmentVariables: Record<
      Exclude<VariableType, VariableType.EVENT_VARIABLE | VariableType.ACTOR_VARIABLE>,
      TokenVariable<VariableToken>[]
    > = _.groupBy(
      ((await this.repository.findMany(query.QueryBuilder.select().from(CollectionNames.ENRICHMENTS).build())) as EnrichmentBase[])
        .filter((e) => e.type != VariableType.VELOCITY)
        .map((e) => ({
          type: e.type,
          variable: {
            type: 'Common Variable',
            variableType: e.type,
            name: e.name,
            properties: e.properties,
            description: e.description,
            args: {},
          },
        })),
      (e: any) => e.type,
    ) as Record<any, any[]>;
    return {
      [VariableType.EVENT_VARIABLE]: eventVariables,
      ...enrichmentVariables,
    };
  };

  public componentWillUnmount() {
    const { expressionView } = this.state;
    expressionView?.terminate();
    this.computation?.stop();
  }

  private onChangeSyntax = (updatedExpression: Syntax): void => {
    const {
      velocityVariable: { velocityVariable },
      changeVelocityVariable,
    } = this.props;
    velocityVariable.groupBy = updatedExpression;
    changeVelocityVariable(velocityVariable);
  };

  private onClose() {}

  private refreshVariables = async () => {
    const { expressionView } = this.state;
    if (expressionView == undefined) return;
    const expression = this.props.velocityVariable.velocityVariable.groupBy;
    const variables: Record<string, TokenVariable[]> = await this.prepareVariables();
    await this.expressionBridge.start(expression, variables);
    expressionView.getContext().setVariables(variables);
  };
}

interface Props extends ReduxProps {
  update: number;
  disabled: boolean;
  velocityUpdated: number;
}

class State {
  expressionView: ExpressionBuilderView | undefined;
  variables: Record<string, TokenVariable[]> = {};
  expression: any = {
    id: 'groupBy',
    value: { type: 'TokenGroup' },
    children: [],
  };
  update: number = 0;
}

const mapStateToProps = (state: RootState) => ({
  velocityVariable: state.velocityVariable as VelocityVariableState,
});

const mapDispatchToProps = { enterComponent, leaveComponent, changeVelocityVariable, clearVelocityVariable, changeVelocityVariableIsUpdate };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(VelocityVariableGroupByComponent);
